import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;

export type Recurrence = 'none' | 'daily' | 'weekly';

export interface MaintenanceWindow {
  id: string;
  organizationId: string;
  divisionId: string | null;
  regionId: string | null;
  name: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  recurrence: Recurrence;
  recurrenceUntil: string | null;
  suppressAlerts: boolean;
  activeNow: boolean;
  createdAt: string;
}

interface MaintenanceWindowsResponse {
  windows: MaintenanceWindow[];
}

export interface CreateMaintenanceWindowArgs {
  name: string;
  description?: string;
  divisionId?: string | null;
  regionId?: string | null;
  startsAt: string;
  endsAt: string;
  recurrence?: Recurrence;
  recurrenceUntil?: string | null;
  suppressAlerts?: boolean;
}

export function useMaintenanceWindows() {
  return useQuery<MaintenanceWindow[]>({
    queryKey: ['maintenance-windows'],
    queryFn: async () => {
      const response = await axios.get<MaintenanceWindowsResponse>(
        `${backendApi}/api/maintenance-windows`,
        { withCredentials: true },
      );
      return response.data.windows;
    },
  });
}

export function useCreateMaintenanceWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateMaintenanceWindowArgs) => {
      const response = await axios.post<MaintenanceWindow>(`${backendApi}/api/maintenance-windows`, args, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
    },
  });
}

export function useDeleteMaintenanceWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/maintenance-windows/${id}`, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
    },
  });
}
